import React, { useCallback, useEffect, useState } from "react";
// ArrowPathIcon : the Retry icon on the load-error exit. Start
// Over carries the same icon, but from inside StartOverButton now, so
// this import no longer serves it.
import { ArrowRightIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import Alert from "../Alert";
import ActionButton from "../ActionButton";
import StartOverButton from "./StartOverButton";
import ChallengePanel from "./ChallengePanel";
import StepAwaySave, { ConfigFileSaveError } from "./StepAwaySave";
import { useConfigFileSave } from "../../hooks/useConfigFileSave";
import {
  apiGetChallenges,
  ApiError,
  SHORT_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../../issuance/apiClient";

/**
 * Step 3 - Verify Domain Ownership.
 *
 * Fetches per-domain challenge details from /api/issuance/sessions/
 * :id/challenges and renders one ChallengePanel for every
 * authorization, including one that is already "valid" when the step
 * loads (see the `details.map` comment). Validation is driven by two
 * SEPARATE manual actions inside each panel (the trigger/poll split - see
 * runTrigger and runCheck in ChallengePanel.jsx) : "Request
 * Validation" asks Sectigo® to validate, pressed once after the record
 * or file is deployed, and "Check Validation Status" is the read-only
 * status poll, safe to press repeatedly. There is no automatic
 * polling. As each domain reports "valid" the panel calls onValidated
 * and the Continue action unlocks once all are done (2026-07-04
 * operator consistency directive : the button is labelled exactly
 * "Continue" at all times ; the disabled state carries the
 * not-yet-complete meaning the old "Verify Every Domain to Continue"
 * label used to spell out).
 *
 * 2026-07-04 Step 3 presentation redesign (operator direction : one
 * method at a time, numbered sequence, no polling) : each
 * ChallengePanel now walks ONE numbered sequence for a single selected
 * method (see the ChallengePanel.jsx header), and every "come back
 * later" affordance on this step - the step-away line and its
 * Configuration File download - is grouped
 * into ONE "Need to Step Away" section so saving progress
 * never competes visually with the per-domain validation actions. The
 * Continue button sits below it in the step's action row, beside Start
 * Over, as the step's single primary action.
 *
 * The customer can download a Trustico® Configuration File at any point
 * here to save their progress - dropping it back on Step 1 later
 * resumes this exact order (DNS propagation can take a while, so this
 * is the supported "come back later" path).
 *
 * Props :
 *   sessionId       - server session id
 *   primaryDomain   - used for the Configuration File download filename
 *   (The authorizations props snapshot is deliberately NOT taken :
 *   every list this step renders is derived from the /challenges
 *   response so they can never disagree with each other.)
 *   onComplete      - called once every pending authorization flips
 *                     to "valid"
 *   onReset         - the page-level Start Over handler (handleReset in
 *                     src/client/pages/Issuance.jsx). Operator directive
 *                     2026-07-05 : every section carries its own
 *                     centered Continue + Start Over row, so the
 *                     trigger moved from the page bottom into each
 *                     step component ; the handler and its
 *                     discard/cleanup logic stay on the page. Start
 *                     Over matters MOST on this step : the
 *                     ChallengePanel guidance for a stalled or
 *                     terminally-invalid order points the customer at
 *                     starting a fresh order, and this is the control
 *                     that does it.
 */
function Step3Verify({ sessionId, primaryDomain, onComplete, onReset }) {
  const [details, setDetails] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // 2026-08-13 go-public cleanup (audit : "functionality-dead-end") :
  // bumping this counter re-runs the challenge-fetch effect below, so
  // a failed load has a real Retry instead of stranding the
  // customer (the old early-return rendered a dismissable Alert whose
  // dismissal dropped onto a permanent "Loading..." placeholder with
  // no retry, no Start Over, and no other control on screen).
  const [reloadNonce, setReloadNonce] = useState(0);
  const [validated, setValidated] = useState(() => new Set());
  // Authorizations that went terminally INVALID during THIS session
  // (2026-08-14 go-public review) : the load-time d.status the intro
  // gate reads never changes after mount, so without this mirror of
  // the `validated` Set a domain failing LIVE kept the numbered-step
  // instructions on screen after its panel had withdrawn them.
  const [invalidated, setInvalidated] = useState(() => new Set());
  // ONE implementation of the Configuration File save, shared with the
  // signing wait on Step 4 (see the hook's header).
  const configSave = useConfigFileSave(sessionId, primaryDomain);

  // Changed-intent disarm for Start Over (2026-08-17 review round 1),
  // the same rule Step 4 applies to its downloads. The step-away save
  // sits DIRECTLY above the action row here, so a customer who arms
  // Start Over and then saves their configuration file instead has
  // visibly chosen to keep the request - and must not be left with a
  // primed button one press from discarding it.
  const [disarmToken, setDisarmToken] = useState(0);
  const saveThenDisarm = useCallback(() => {
    setDisarmToken((n) => n + 1);
    return configSave.save();
  }, [configSave]);

  // Fetch the DNS-01 / HTTP-01 challenge details for every
  // authorization on mount. The server returns a per-domain array
  // with the record values already computed.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    // Bounded load (2026-08-14 go-public review, the "functionality-wedge"
    // family's last Step 3 member) : a challenge fetch that neither
    // resolved nor rejected left this step on "Loading Domain
    // Challenge Details..." forever with ZERO controls - the Retry
    // recovery below only renders once loadError is set, so an
    // unbounded hang made the recovery path unreachable. The abort
    // lands in the catch as a branded retryable loadError.
    // The controller is captured out of withTimeoutSignal (which
    // invokes its callback synchronously, before any await) so the
    // effect cleanup below can abort an in-flight load on unmount.
    let loadController = null;
    (async () => {
      try {
        const fetched = await withTimeoutSignal(
          SHORT_CALL_TIMEOUT_MS,
          (signal, controller) => {
            loadController = controller;
            return apiGetChallenges(sessionId, { signal });
          }
        );
        // 2026-07-03 PR #76 seventh-pass review #3 : apiClient `call`
        // resolves an off-contract 200-with-empty-body to null. A null
        // here would setDetails(null), leaving the component stuck on the
        // "Loading Domain Challenge Details..." spinner FOREVER (the load
        // completed but `details` stays null). Treat it as a load error
        // so the customer gets an actionable message instead of an
        // infinite spinner. (An empty array is a distinct, valid case and
        // is preserved.)
        if (!fetched) {
          throw new Error("The server returned no challenge details.");
        }
        // 2026-07-04 CI review 4881104837 finding #4 : everything after
        // the loading branch assumes `details` is an ARRAY
        // (details.filter for pendingDetails, pendingDetails.every for
        // the Continue gate). A truthy NON-array (an off-contract
        // object body) would pass the null-guard above and crash the
        // render with a raw TypeError instead of this component's
        // house-style Alert. Reject it the same way the null case is
        // rejected - a sibling sentence down the same loadError
        // path.
        if (!Array.isArray(fetched)) {
          throw new Error(
            "The server returned challenge details that could not be read."
          );
        }
        if (!cancelled) setDetails(fetched);
      } catch (e) {
        if (!cancelled) {
          // The fallback string is the only copy we produce
          // ourselves, a plain sentence like every customer error
          // since 2026-08-27 ; ApiError messages arrive as sentences
          // from the server. The AbortError from the
          // bounded controller above is our own timeout - its raw
          // lowercase browser prose maps to the branded retryable
          // line (2026-08-14 go-public review).
          setLoadError(
            e && e.name === "AbortError"
              ? "Loading the domain challenge details timed out. Please try again."
              : e instanceof ApiError
                ? e.message
                : "The validation details could not be loaded. " +
                  (e.message || "Unknown error.")
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      // Abort an in-flight load on unmount / StrictMode remount ; the
      // helper's own finally clears the timer. A completed call makes
      // this a no-op.
      if (loadController) loadController.abort();
    };
    // reloadNonce is the Retry re-fetch trigger (see its state
    // comment above) : bumping it re-runs this effect against the
    // same sessionId.
  }, [sessionId, reloadNonce]);

  /**
   * Retry for a failed challenge load : clear the error AND
   * re-arm the loading placeholder, then bump the nonce so the fetch
   * effect re-runs. Clearing loadError alone (the old Alert onClose)
   * left details null with the effect already spent - the exact
   * dead-end this handler exists to close.
   */
  const handleRetryLoad = () => {
    setLoadError(null);
    setDetails(null);
    setReloadNonce((n) => n + 1);
  };

  const handleValidated = (authUrl) => {
    setValidated((prev) => {
      const next = new Set(prev);
      next.add(authUrl);
      return next;
    });
  };

  // Sibling of handleValidated for the live-invalid transition - see
  // the `invalidated` state comment above.
  const handleInvalid = (authUrl) => {
    setInvalidated((prev) => {
      const next = new Set(prev);
      next.add(authUrl);
      return next;
    });
  };

  if (loadError) {
    return (
      <>
        {/*
          2026-08-13 go-public cleanup (audit : "functionality-dead-end") :
          the load failure keeps its own action row. The Alert carries
          NO onClose - dismissing it used to drop the customer onto a
          permanent "Loading..." placeholder with zero controls, since
          the fetch effect had already spent itself. Retry re-runs
          the fetch (handleRetryLoad) and Start Over remains the
          always-available exit, mirroring the step's standard row.
        */}
        <Alert
          type="error"
          title="Unable to Load Domain Challenge Details"
          live
        >
          {loadError}
        </Alert>
        <div className="flex justify-center mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <ActionButton
              onClick={handleRetryLoad}
              variant="primary"
              icon={ArrowPathIcon}
            >
              Retry
            </ActionButton>
            <StartOverButton onReset={onReset} disarmToken={disarmToken} />
          </div>
        </div>
      </>
    );
  }

  if (!details) {
    return (
      // role="status" + aria-live (2026-08-14 go-public accessibility
      // review) : the load window renders no controls, so without an
      // announcement a screen-reader customer lands on a silently
      // empty step - same posture as the page-level restore
      // placeholder (review I10).
      <p
        role="status"
        aria-live="polite"
        className="text-muted py-4 text-center"
      >
        Loading Domain Challenge Details...
      </p>
    );
  }

  const pendingDetails = details.filter((d) => d.status !== "valid");
  // 2026-06-01 third-round code review #8 : derive allDone from
  // pendingDetails (the server's source of truth that the
  // ChallengePanel components also iterate) rather than the
  // authorizations props snapshot taken when Step 2 completed
  // (formerly filtered into a pendingMeta memo, removed as dead code
  // by the 2026-07-04 PR #76 CI review once nothing read it).
  // If the two sources ever drift (a freshly-valid auth dropped
  // from /challenges, or a new auth added) the Continue button
  // now locks / unlocks in step with what the customer sees
  // rendered.
  //
  // 2026-05-31 seventh-round code review I5 : Array.prototype.every on
  // an empty array returns true (vacuous truth), so the guard is on
  // `details.length > 0`, not `pendingDetails.length > 0`.
  //
  // PR #76 review (second pass) corrected the earlier gate : it used
  // `pendingDetails.length > 0`, which kept Continue permanently
  // DISABLED when a session was restored at Step 3 after every
  // authorization had already flipped to "valid" (pendingDetails
  // empty) - a fully-validated customer stranded with no way forward.
  // Gating on `details.length > 0` instead enables Continue in that
  // case (everything is validated, so the customer proceeds to Step 4,
  // looking at one card per domain each carrying its validated line -
  // see the `details.map` comment below), while STILL keeping Continue
  // disabled in the genuinely empty `details.length === 0` case the I5
  // note was defending against.
  const allDone =
    details.length > 0 && pendingDetails.every((d) => validated.has(d.authUrl));

  return (
    <>
      {/*
        2026-07-04 live-run fix : this paragraph still described the old
        fused status button after the trigger/poll split (the
        commit "split Step 3 validation into separate trigger and poll
        actions"). Rewritten to walk the real two-button flow : deploy
        first, "Request Validation" once, then wait and poll with the
        read-only "Check Validation Status".

        2026-07-04 Step 3 presentation redesign (operator direction :
        one method at a time, numbered sequence, no polling) : rewritten
        again to describe the per-panel method selector and numbered
        sequence, while keeping the trigger-once / poll-repeatedly
        guidance the live-run fix introduced.
      */}
      {/*
        Gated on at least one panel still being ACTIONABLE (2026-08-14
        go-public review, widened from details.length > 0) : the
        zero-domain empty state below otherwise rendered UNDER these
        numbered-step instructions, telling the customer to work
        panels that do not exist - and when every remaining panel is
        terminally invalid (or already valid), the panels have
        withdrawn their interactive sequence, so instructions to
        "press Request Validation" would describe controls that are
        not on the page.
      */}
      {pendingDetails.some(
        (d) =>
          d.status !== "invalid" &&
          !invalidated.has(d.authUrl) &&
          !validated.has(d.authUrl)
      ) && (
        /* 2026-08-15 design overhaul (operator-approved mockup) : ONE
           explanatory lede that says WHY validation exists and what
           happens when it completes - the per-button mechanics now
           live beside the buttons themselves inside each panel, so
           this paragraph no longer restates them. */
        <p data-testid="verify-instructions" className="text-body mb-6">
          Each domain below has its own panel where you can choose a validation
          method, add the displayed record or file to your own infrastructure,
          then ask the Certificate Authority (CA) to check it. When every domain
          has been successfully validated, you can move on to the next step,
          where your SSL Certificate is issued. Wildcard domains support DNS-01
          validation only.
        </p>
      )}

      {/*
        2026-08-13 go-public cleanup (audit : "missing-empty-state") :
        a zero-domain response previously rendered the numbered-steps
        instructions above with NO panels under them and a Continue
        that could never enable (allDone requires details.length > 0),
        stranding the customer with nothing actionable. The explicit
        state names the situation and points at Start Over, which stays
        enabled on this step at all times.
      */}
      {details.length === 0 && (
        <Alert type="warning" title="Nothing to Verify" className="mb-4">
          This issuance request has no domains waiting to be verified. It may
          have already been completed or expired. Please start a new issuance
          request.
        </Alert>
      )}

      {/*
        EVERY DOMAIN GETS A CARD, including one already valid when Step
        3 loads (operator directive, 2026-08-22 : "I am trying to keep
        this as consistent as possible and by you changing the format
        and having additional messaging boxes in different situations
        it becomes complicated").

        This used to map pendingDetails, so a domain the server already
        reported valid drew NO card and was named only in a separate
        green Alert above - a THIRD presentation, on top of the full
        card while pending and the collapsed one after validating in
        session. Mapping `details` gives one card per domain for the
        whole step, and ChallengePanel's mount-time mapping already
        turns entry.status "valid" into its validated state, so such a
        card arrives showing the validated line with no sequence and no
        buttons.

        pendingDetails is still what the Continue gate and the
        instruction lede are computed from : those ask "is there work
        left", which is a different question from "what do we show".
      */}
      {details.map((entry) => (
        <ChallengePanel
          key={entry.authUrl}
          sessionId={sessionId}
          entry={entry}
          onValidated={handleValidated}
          onInvalid={handleInvalid}
        />
      ))}

      {/* 2026-07-04 Step 3 presentation redesign (operator direction :
          one method at a time, numbered sequence, no polling) : every
          "come back later" affordance - the guidance Alert (formerly
          titled "Need to Come Back Later?"), the save-failure Alert,
          and the Configuration File download that used to sit BESIDE
          the Continue button - lives in this one section, so saving
          progress never competes visually with the validation actions
          above or the Continue action below. A <section> element (not
          a plain div) so the grouping is queryable by landmark in
          tests and assistive technology.
          2026-08-13 go-public flattening (operator-directed : "avoiding
          boxes in boxes") : the section's own border/rounded/bg frame
          is dissolved - its fill was byte-identical to the StepPanel
          .card it sits on, so it drew a hairline box on an identical
          surface and started the page's deepest nesting chain (card >
          white box > blue Alert > blue reveal box > white chip). The
          heading + paragraph + content now flow directly on the card,
          the CSRGenerator section idiom. */}
      {/* 2026-08-15 design overhaul (operator-approved mockup +
          verbatim wording) : the Need to Step Away section collapses to
          ONE line whose action is a link. Left-aligned, like every
          other sentence on the page : centered long-form text was the
          specific thing the operator called hard to read, and
          Step3Verify.test.jsx pins not.toContain("text-center"). The
          lifetime + treat-like-a-password sentence sits directly under
          the line, still interpolating SESSION_LIFETIME_HOURS so it can
          never drift from the real expiry. The section landmark +
          accessible name are kept for assistive technology and the
          suite's landmark queries. */}
      {/* mt-6, the step's own block gap : the challenge panels above
            and the Continue row below both use it, and this section was
            the one 32px gap on the step (operator directive, 2026-08-23
            : all line spacing between elements is the same). */}
      <section aria-label="Need to Step Away" className="mt-6">
        <ConfigFileSaveError
          error={configSave.error}
          onClose={configSave.clearError}
        />
        <StepAwaySave saving={configSave.saving} save={saveThenDisarm} />
      </section>

      {/*
        2026-07-04 operator consistency directive ("buttons should be
        standard Continue and Start Over") : the label is ALWAYS
        exactly "Continue" - the former state-dependent pair
        ("Continue to Download SSL Certificate" when ready, "Verify
        Every Domain to Continue" while locked) is retired. The
        disabled state now carries the not-yet-complete meaning on its
        own, mirroring the greyed-out-until-complete posture of the
        other tool pages.
      */}
      {/*
        Operator directive 2026-07-05 ("Always there should be a
        Continue and Start Over button in EACH section... you have
        instead thrown this at the bottom of the page where even I
        can't see it") : the step's own centered action row - Continue
        (primary, disabled until every domain validates) beside Start
        Over (outline = the secondary style). Start Over stays ENABLED
        here at all times : a customer stuck on a stalled order is
        exactly who needs it (the ChallengePanel dead-order guidance
        points the customer at starting a new issuance request without
        naming the control, and this is the control that does it).
        2026-08-13 go-public flattening : the pair sits in the
        equal-width two-column grid every other tool page's paired
        action row shares, replacing the content-sized flex row that
        rendered the two buttons at visibly different widths.
      */}
      <div className="flex justify-center mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <ActionButton
            onClick={onComplete}
            disabled={!allDone}
            variant="primary"
            icon={ArrowRightIcon}
          >
            Continue
          </ActionButton>
          <StartOverButton onReset={onReset} disarmToken={disarmToken} />
        </div>
      </div>
    </>
  );
}

export default Step3Verify;
